import { useState, useEffect } from "react"
import Value from "aldoo-ra/Value"
import { PermissionAPI } from "./api"

/**
 * Hook to check permission - always fresh from server
 * @param {string} permission - Permission to check
 * @returns {Object} - {hasPermission: boolean, loading: boolean}
 */
export const useHasPermission = (permission) => {
  const [auth] = Value("user-credentials")
  const [hasPermission, setHasPermission] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkPermission = async () => {
      if (!auth?._id || !permission) {
        setHasPermission(false)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const result = await PermissionAPI.hasPermission(auth._id, permission)
        setHasPermission(result)
      } catch (error) {
        console.error("Permission check failed:", error)
        setHasPermission(false)
      } finally {
        setLoading(false)
      }
    }

    checkPermission()
  }, [auth, permission])

  return { hasPermission, loading }
}

/**
 * Hook to check multiple permissions - always fresh from server
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {Object} - {hasAnyPermission: boolean, loading: boolean}
 */
export const useHasAnyPermission = (permissions) => {
  const [auth] = Value("user-credentials")
  const [hasAnyPermission, setHasAnyPermission] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkPermissions = async () => {
      if (!auth?._id || !permissions?.length) {
        setHasAnyPermission(false)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const result = await PermissionAPI.hasPermission(auth._id, permissions)
        setHasAnyPermission(result)
      } catch (error) {
        console.error("Permission check failed:", error)
        setHasAnyPermission(false)
      } finally {
        setLoading(false)
      }
    }

    checkPermissions()
  }, [auth, permissions])

  return { hasAnyPermission, loading }
}

/**
 * Hook to check all permissions - always fresh from server
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {Object} - {hasAllPermissions: boolean, loading: boolean}
 */
export const useHasAllPermissions = (permissions) => {
  const [auth] = Value("user-credentials")
  const [hasAllPermissions, setHasAllPermissions] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkPermissions = async () => {
      if (!auth?._id || !permissions?.length) {
        setHasAllPermissions(false)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // Check each permission individually
        const results = await Promise.all(
          permissions.map(perm => PermissionAPI.hasPermission(auth._id, perm))
        )
        setHasAllPermissions(results.every(result => result === true))
      } catch (error) {
        console.error("Permission check failed:", error)
        setHasAllPermissions(false)
      } finally {
        setLoading(false)
      }
    }

    checkPermissions()
  }, [auth, permissions])

  return { hasAllPermissions, loading }
}

/**
 * Hook to get all user permissions - always fresh from server
 * @returns {Object} - {permissions: Array<string>, loading: boolean}
 */
export const useUserPermissions = () => {
  const [auth] = Value("user-credentials")
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!auth?._id) {
        setPermissions([])
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const userPermissions = await PermissionAPI.getUserPermissions(auth._id)
        setPermissions(userPermissions)
      } catch (error) {
        console.error("Failed to fetch user permissions:", error)
        setPermissions([])
      } finally {
        setLoading(false)
      }
    }

    fetchPermissions()
  }, [auth])

  return { permissions, loading }
}